<template>
  <BaseModalLarge
    :routes="routes"
    parentRoute="products-stock-orders"
    :label="stockOrder?.status"
    :heading="heading"
    :details="modalDetails"
    :actions="modalActions"
    @action="onActionClick"
  >
    <CreateNoteModal
      v-if="showNoteModal"
      :currentNote="stockOrder?.notes || undefined"
      @submit="handleNoteUpdate($event, 'create')"
      @close="showNoteModal = false"
    />
    <div :class="$style.inner">
      <Details
        v-if="route.name === 'stock-details'"
        :stockOrder="stockOrder"
        :totalAmount="totalAmount"
      />
      <Process
        v-if="processRoute"
        :status="stockOrder?.status"
        :processItems="processItems"
        @updateQuantityReceived="updateQuantityReceived"
      />
    </div>
    <template
      v-if="route.name === 'stock-process'"
      #footer
    >
      <ProcessActions :processItems="processItems" />
    </template>
  </BaseModalLarge>
</template>

<script
  setup
  lang="ts"
>
import { useQuery } from '@vue/apollo-composable';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import config from '@/config';
import filters from '@/filters';
import CreateNoteModal from '@/modals/CreateNote.vue';
import { useCompanyStore } from '@/stores/company';
import type { StockOrder, StockOrderItem } from '@/types';
import Details from './Details.vue';
import { GET_STOCK_ORDER } from './graphql';
import Process from './Process.vue';
import ProcessActions from './ProcessActions.vue';
import useUpdateNote from './updateNote';

const { t } = useI18n();
const { company } = useCompanyStore();
const route = useRoute();
const router = useRouter();

const processItems = ref<StockOrderItem[]>([]);
const showNoteModal = ref(false);

const { result, onResult } = useQuery(
  GET_STOCK_ORDER,
  {
    id: Number.parseInt(route.params.stockOrderId as string),
  },
  {
    fetchPolicy: 'cache-and-network',
  },
);

const { updateNote } = useUpdateNote();

const stockOrder = computed(() => result.value?.stockOrder);

onResult(() => {
  addProcessItems();
});

const heading = computed(() => {
  const { value } = stockOrder;

  if (value?.cid && value?.supplier && value?.supplier.name) {
    return t('products.order_name', {
      cid: value.cid,
      supplier: value.supplier.name,
    });
  }

  return '';
});

const routes = computed(() => [
  {
    name: 'stock-details',
    label: t('global.order'),
    heading: heading.value,
  },
]);

const totalAmount = computed(() => {
  if (stockOrder.value?.orderItems?.length) {
    const totalAmount = stockOrder.value.orderItems
      .map((item: StockOrderItem) => item.total)
      .reduce((a: number, b: number) => a + b);
    return totalAmount;
  } else {
    return 0;
  }
});

const processRoute = computed(() => route.name === 'stock-process');

const isPaidCompleteOrBackOrder = computed(() => {
  const { value } = stockOrder;
  if (value) {
    return (
      value.status === 'paid' ||
      value.status === 'complete' ||
      value.status === 'backorder'
    );
  }

  return false;
});

const addProcessItems = () => {
  const order = stockOrder.value;
  if (order?.orderItems) {
    order.orderItems.forEach((item: StockOrderItem) => {
      const existingItem = processItems.value.find(
        (i) => i.product?.id === item.product?.id,
      );
      if (!existingItem) {
        processItems.value.push({
          id: item.id,
          costPrice: item.costPrice,
          quantity: item.quantity,
          quantityReceived: item.quantityReceived,
          total: item.total,
          product: item.product,
        });
      }
    });
  }
};

const updateQuantityReceived = ({
  id,
  quantityReceived,
}: {
  id: number;
  quantityReceived: number;
}) => {
  const item = processItems.value.find((item) => item.id === id);
  if (item) {
    item.quantityReceived = quantityReceived;
  }
};

const modalDetails = computed(() => {
  const { value } = stockOrder;
  let companyDetails = company.name;

  if (company.address && company.city) {
    companyDetails += `\n${company.address}, ${company.city}`;
  }

  const details = [
    {
      label: t('products.ordered_for'),
      value: companyDetails,
      type: 'html',
    },
  ];

  if (value) {
    details.push(
      {
        label: t('global.total'),
        value: filters.currency(totalAmount.value),
        type: 'text',
      },
      {
        label: t('global.created'),
        value: filters.dateTime(value.createdAt),
        type: 'text',
      },
      {
        label: t('global.ordered'),
        value: filters.dateTime(value.orderedAt),
        type: 'text',
      },
    );
  }

  return details;
});

const modalActions = computed(() => {
  const { value } = stockOrder;

  return !processRoute.value && value
    ? [
        {
          name: 'edit',
          icon: 'edit',
          label: t('global.actions.edit'),
          hide: isPaidCompleteOrBackOrder.value,
        },
        {
          name: 'add-note',
          icon: 'attach',
          hide: !!value.notes,
          label: filters.capitalize(t('global.actions.add_note')),
        },
        {
          name: 'export-csv',
          icon: 'file',
          label: 'CSV',
          href: `${config.backendUrl}/api/stock_orders/download.csv?id=${value.id}`,
        },
        {
          name: 'export-pdf',
          icon: 'pdf',
          label: t('global.pdf'),
          href: `${config.backendUrl}/stock_orders/${value.id}`,
        },
      ].filter((item) => !item.hide)
    : [];
});

const onActionClick = (action: string) => {
  if (action === 'edit') {
    router.push({ name: 'products-order-build' });
  } else if (action === 'add-note') {
    showNoteModal.value = true;
  }
};

const handleNoteUpdate = (
  notes: string,
  action: 'create' | 'edit' | 'delete',
) => {
  if (stockOrder.value) {
    updateNote(stockOrder.value.id, notes, stockOrder.value.status, action);
    showNoteModal.value = false;
  }
};
</script>

<style
  lang="scss"
  module
>
.inner {
  position: relative;
  min-height: 200px;
  height: 100%;
}
</style>
